import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    deleteUserStoreValueSuccess,
    loadUserStore,
    userStoreEntryMirrored,
    userStoreEntryUnmirrored,
    userStoreFailed,
    userStoreLoaded,
    userStoreLoadedFromStorage,
} from '../actions/user-store.actions';
import {
    initialUserStoreState,
    UserStoreEntriesState,
    UserStorePersistedState,
    UserStoreState,
} from '../user-store.state';

/** Marks the slice as loading while the bulk backend GET is in flight. */
export function setUserStoreLoadingReducer(state: Readonly<UserStoreState>): Readonly<UserStoreState> {
    return { ...state, status: LoadStatus.Loading };
}

/** Populates the slice from a successful bulk backend load and records that the backend has answered. */
export function setUserStoreEntriesReducer(
    state: Readonly<UserStoreState>,
    { entries }: { entries: UserStoreEntriesState },
): Readonly<UserStoreState> {
    return { ...state, entries, status: LoadStatus.Loaded, hasBackendSettled: true };
}

/**
 * Populates the slice from the persisted cache — both the mirrored entries and the pending-delete
 * retry queue, see {@link UserStorePersistedState}. Deliberately does NOT set `hasBackendSettled`
 * — a cache hit is not the backend answering (see `UserStoreState`'s doc comment).
 */
export function setUserStoreEntriesFromStorageReducer(
    state: Readonly<UserStoreState>,
    { entries, pendingDeletes }: UserStorePersistedState,
): Readonly<UserStoreState> {
    return { ...state, entries, pendingDeletes, status: LoadStatus.Loaded };
}

/**
 * Marks the bulk load as failed. `entries` is LEFT IN PLACE so cached values survive a failed
 * refresh; a failure still settles the backend question, so an offline user gets the cached
 * mirror rather than an indefinite wait.
 */
export function setUserStoreErrorReducer(state: Readonly<UserStoreState>): Readonly<UserStoreState> {
    return { ...state, status: LoadStatus.Error, hasBackendSettled: true };
}

/**
 * Writes one value into the mirror. Only dispatched by `UserStoreEffects` after it has confirmed
 * the key is not in `USER_STORE_UNMIRRORED_KEYS` — see `userStoreEntryMirrored`'s doc comment.
 *
 * Also clears `key` out of `pendingDeletes`, if present: a delete only ever gets queued there for
 * something the user asked to remove, and mirroring a value for the same key means the user just
 * asked to have it back — that makes any earlier pending delete obsolete by definition, whether or
 * not it has been retried yet. Handling this here, in the reducer, rather than in an effect,
 * matters: an effect reacting to `userStoreEntryMirrored` would still race
 * `retryPendingUserStoreDeletesRemote$`, whereas clearing it in the same state update that adds the
 * key back closes that race entirely — there is no window in which the state simultaneously holds
 * the fresh value AND a stale pending delete for it.
 */
export function mirrorUserStoreEntryReducer(
    state: Readonly<UserStoreState>,
    { key, value }: { key: string; value: unknown },
): Readonly<UserStoreState> {
    return {
        ...state,
        entries: { ...state.entries, [key]: value },
        pendingDeletes: state.pendingDeletes.filter((pendingKey) => pendingKey !== key),
    };
}

/**
 * Removes one value from the mirror and adds it to `pendingDeletes` in the same step — a mirrored
 * key's delete always needs the retry queue, since `UserStoreApiService.deleteEntry` bypasses the
 * offline request queue (see `pendingDeletes`'s doc comment in `user-store.state.ts`). Idempotent:
 * removing an absent key is a no-op on `entries`, and re-adding an already-pending key is a no-op
 * on `pendingDeletes`.
 */
export function unmirrorUserStoreEntryReducer(
    state: Readonly<UserStoreState>,
    { key }: { key: string },
): Readonly<UserStoreState> {
    return {
        ...state,
        entries: Object.fromEntries(Object.entries(state.entries).filter(([entryKey]) => entryKey !== key)),
        pendingDeletes: state.pendingDeletes.includes(key) ? state.pendingDeletes : [...state.pendingDeletes, key],
    };
}

/**
 * Clears `key` out of `pendingDeletes` once its DELETE has been confirmed by the backend
 * (including a 404, treated as success — see `UserStoreApiService.deleteEntry`). Idempotent:
 * clearing an absent key is a no-op, which covers the excluded-key case where `key` was never
 * added in the first place (see `USER_STORE_UNMIRRORED_KEYS`).
 */
export function clearPendingUserStoreDeleteReducer(
    state: Readonly<UserStoreState>,
    { key }: { key: string },
): Readonly<UserStoreState> {
    return { ...state, pendingDeletes: state.pendingDeletes.filter((pendingKey) => pendingKey !== key) };
}

const createUserStoreReducer = createReducer(
    initialUserStoreState,
    on(loadUserStore, setUserStoreLoadingReducer),
    on(userStoreLoaded, setUserStoreEntriesReducer),
    on(userStoreLoadedFromStorage, setUserStoreEntriesFromStorageReducer),
    on(userStoreFailed, setUserStoreErrorReducer),
    on(userStoreEntryMirrored, mirrorUserStoreEntryReducer),
    on(userStoreEntryUnmirrored, unmirrorUserStoreEntryReducer),
    on(deleteUserStoreValueSuccess, clearPendingUserStoreDeleteReducer),
);

export function userStoreReducer(
    state: Readonly<UserStoreState> | undefined,
    action: Action,
): Readonly<UserStoreState> {
    return createUserStoreReducer(state, action);
}
